import { Box, Button, Checkbox, Flex, Grid, Input, Text, useToast } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { FaCalendarDay, FaExclamationTriangle, FaFileExcel, FaFilePdf, FaMoneyCheckAlt } from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import Layout from "../../../components/Layout";
import { allowedCompanyValues, companyOptions, useCompany } from "../../../contexts/CompanyContext";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import {
  ExchangeInstallment,
  ExchangeRow,
  ExchangeSituation,
  ExchangeView,
  ManagementExchange,
} from "../../../models/GestaoOperations";
import { exportExchange, getExchange } from "../../../services/gestaoOperationsService";
import { crm, Pill, ResponsibleChip } from "../../../theme";
import {
  Card,
  Column,
  DataTable,
  ErrorState,
  filterInputProps,
  fmtDate,
  fmtMoney,
  KpiTile,
  Loading,
  openProcessPath,
  PageHeader,
  ProcessCell,
  SegmentedTabs,
  SelectFilter,
} from "../operacoes/ui";

const viewItems: { key: ExchangeView; label: string }[] = [
  { key: "hoje", label: "Hoje" },
  { key: "semana", label: "Nesta semana" },
  { key: "proxima", label: "Próxima semana" },
  { key: "aberto", label: "Em aberto" },
  { key: "atraso", label: "Em atraso" },
  { key: "proximos", label: "Próximos" },
  { key: "aguardando", label: "Aguardando data" },
  { key: "concluidos", label: "Concluídos" },
  { key: "todos", label: "Todos" },
];

const situationStyle: Record<ExchangeSituation, { text: string; bg: string; fg: string }> = {
  atrasado: { text: "Em atraso", bg: "#fef2f2", fg: crm.danger },
  vence_hoje: { text: "Vence hoje", bg: "#fff7ed", fg: "#c2410c" },
  a_vencer: { text: "A vencer", bg: crm.brandSoft, fg: crm.brand },
  aguardando_data: { text: "Aguardando data", bg: "#fffbeb", fg: crm.warning },
  concluido: { text: "Concluído", bg: "#f0fdf4", fg: crm.success },
  sem_parcelas: { text: "Sem parcelas", bg: crm.subtle, fg: crm.textMuted },
};

const InstallmentCell = ({ item }: { item?: ExchangeInstallment }) => {
  if (!item) return <Text color={crm.textFaint}>—</Text>;
  return (
    <Box>
      <Text fontWeight="600" color={item.paid ? crm.success : crm.text}>
        {fmtDate(item.dueDate)}
      </Text>
      <Text fontSize="11px" color={crm.textMuted}>
        {fmtMoney(item.amount, item.currency)}
      </Text>
      <Text fontSize="11px" color={item.paid ? crm.success : crm.warning}>
        {item.paid ? `Pago${item.paidAt ? ` em ${fmtDate(item.paidAt)}` : ""}` : "Em aberto"}
      </Text>
    </Box>
  );
};

const dueHint = (days?: number) => {
  if (days === undefined || days === null) return "";
  if (days === 0) return "hoje";
  if (days > 0) return `em ${days} dia${days === 1 ? "" : "s"}`;
  return `${Math.abs(days)} dia${days === -1 ? "" : "s"} em atraso`;
};

const Cambio = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const navigate = useNavigate();
  const toast = useToast();
  const { currentCompany, currentEmulation } = useCompany();
  const auth = useAuthUser<any>();
  const allowedUnits = allowedCompanyValues(auth?.company);

  const [unit, setUnit] = useState<string>(currentCompany && currentCompany !== 5 ? String(currentCompany) : "");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("");
  const [responsible, setResponsible] = useState("");
  const [view, setView] = useState<ExchangeView>("aberto");
  const [includeClosed, setIncludeClosed] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [data, setData] = useState<ManagementExchange | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    setUnit(currentCompany && currentCompany !== 5 ? String(currentCompany) : "");
  }, [currentCompany]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const [exporting, setExporting] = useState<"xlsx" | "pdf" | null>(null);
  const filters = () => ({
    type: unit ? Number(unit) : undefined,
    companyId: currentEmulation,
    search,
    clientId,
    responsible,
    view,
    includeClosed: includeClosed || undefined,
  });

  const runExport = async (format: "xlsx" | "pdf") => {
    setExporting(format);
    try {
      await exportExchange(filters(), format);
    } catch {
      toast({ title: "Não foi possível exportar.", status: "error" });
    } finally {
      setExporting(null);
    }
  };

  const load = async () => {
    const current = ++seq.current;
    setLoading(true);
    setError(false);
    try {
      const result = await getExchange({
        type: unit ? Number(unit) : undefined,
        companyId: currentEmulation,
        search,
        clientId,
        responsible,
        view,
        includeClosed: includeClosed || undefined,
        page,
        pageSize,
      });
      if (current !== seq.current) return;
      setData(result);
    } catch (e) {
      if (current !== seq.current) return;
      console.log("error", e);
      setError(true);
      toast({ title: "Não foi possível carregar o câmbio.", status: "error" });
    } finally {
      if (current === seq.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [unit, search, clientId, responsible, view, includeClosed, page, pageSize, currentEmulation]);

  const reset = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPage(1);
  };

  const columns: Column<ExchangeRow>[] = [
    { header: "Processo / cliente", render: (r) => <ProcessCell ref_={r.ref} client={r.clientName} urgent={r.isUrgent} /> },
    {
      header: "Situação",
      render: (r) => {
        const s = situationStyle[r.situation];
        return (
          <Box>
            <Pill bg={s.bg} color={s.fg}>{s.text}</Pill>
            {r.situation !== "concluido" && r.situation !== "aguardando_data" && r.nextInDays !== undefined && (
              <Text fontSize="11px" color={crm.textMuted} mt="2px">{dueHint(r.nextInDays)}</Text>
            )}
          </Box>
        );
      },
    },
    {
      header: "Próximo pagamento",
      render: (r) =>
        r.next ? (
          <Box>
            <Text fontWeight="700" color={r.situation === "atrasado" ? crm.danger : crm.text}>{fmtDate(r.next.dueDate)}</Text>
            <Text fontSize="11px" color={crm.textMuted}>{fmtMoney(r.next.amount, r.next.currency)}</Text>
          </Box>
        ) : (
          <Text color={crm.textFaint}>—</Text>
        ),
    },
    { header: "1º pagamento", render: (r) => <InstallmentCell item={r.installments.find((i) => i.number === 1) ?? r.installments[0]} /> },
    {
      header: "2º pagamento",
      render: (r) => {
        const second = r.installments.find((i) => i.number === 2) ?? (r.installments[0]?.number === 1 ? undefined : r.installments[1]);
        const extra = r.installments.filter((i) => i.number !== undefined && i.number > 2).length;
        return (
          <Box>
            <InstallmentCell item={second} />
            {extra > 0 && <Text fontSize="11px" color={crm.textMuted}>+{extra} parcela(s)</Text>}
          </Box>
        );
      },
    },
    {
      header: "Numerário",
      render: (r) => {
        const item = r.next;
        if (!item) return <Text color={crm.textFaint}>—</Text>;
        if (item.numeraryReceivedAt) return <Pill bg="#ecfdf3" color={crm.success}>Recebido {fmtDate(item.numeraryReceivedAt)}</Pill>;
        if (item.numeraryRequestedAt) return <Pill bg="#fffbeb" color={crm.warning}>Solicitado {fmtDate(item.numeraryRequestedAt)}</Pill>;
        return <Pill bg={crm.subtle} color={crm.textMuted}>Não solicitado</Pill>;
      },
    },
    { header: "Responsável", render: (r) => <ResponsibleChip name={r.responsibleName} /> },
    {
      header: "",
      render: (r) => (
        <Button size="xs" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} onClick={(e) => { e.stopPropagation(); navigate(openProcessPath(r.processId)); }}>
          Ver ficha
        </Button>
      ),
    },
  ];

  const k = data?.kpis;

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="auto">
      <Layout>
        <Box p="24px" bg={crm.pageBg} minH="100%">
          <PageHeader title="Câmbio e pagamentos" subtitle="Programação das parcelas de câmbio dos processos em andamento." />

          <Card p="16px">
            <Flex gap="12px" flexWrap="wrap" align="flex-end">
              <Box flex="1" minW="240px">
                <Text fontSize="11px" fontWeight="600" color={crm.textMuted} mb="3px" textTransform="uppercase">
                  Busca
                </Text>
                <Input
                  {...filterInputProps}
                  placeholder="Referência, cliente, DI, container ou BL"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Box>
              <SelectFilter
                label="Unidade"
                allLabel="Todas"
                value={unit}
                onChange={reset(setUnit)}
                options={companyOptions
                  .filter((c) => c.value !== 5 && (allowedUnits == null || allowedUnits.includes(c.value)))
                  .map((c) => ({ value: String(c.value), label: c.label }))}
              />
              <SelectFilter
                label="Cliente"
                value={clientId}
                onChange={reset(setClientId)}
                options={(data?.filters.clients ?? []).map((c) => ({ value: c.id ?? "", label: c.name }))}
              />
              <SelectFilter
                label="Responsável"
                value={responsible}
                onChange={reset(setResponsible)}
                options={(data?.filters.responsibles ?? []).map((r) => ({ value: r, label: r }))}
              />
              <Checkbox
                size="sm"
                isChecked={includeClosed}
                onChange={(e) => {
                  setIncludeClosed(e.target.checked);
                  setPage(1);
                }}
                pb="6px"
              >
                <Text fontSize="sm" color={crm.textMuted}>Incluir encerrados</Text>
              </Checkbox>
            </Flex>
          </Card>

          {!data && loading && <Loading />}
          {!data && !loading && error && <ErrorState onRetry={load} />}

          {data && k && (
            <Box mt="16px" opacity={loading ? 0.6 : 1} transition="opacity .15s">
              <Grid templateColumns={isMobile ? "1fr" : "repeat(3, 1fr)"} gap="16px" mb="16px">
                <KpiTile
                  title="Câmbios em aberto"
                  value={k.abertos}
                  icon={<FaMoneyCheckAlt />}
                  hint="Processos com parcela não paga"
                  onClick={() => reset(setView)("aberto")}
                />
                <KpiTile
                  title="Em atraso"
                  value={k.emAtraso}
                  icon={<FaExclamationTriangle />}
                  tone={k.emAtraso > 0 ? "danger" : "default"}
                  hint="Parcela vencida e não paga"
                  onClick={() => reset(setView)("atraso")}
                />
                <KpiTile
                  title="Próximo vencimento"
                  value={k.proximoVencimento.date ? fmtDate(k.proximoVencimento.date) : "—"}
                  icon={<FaCalendarDay />}
                  hint={k.proximoVencimento.date ? `${k.proximoVencimento.count} parcela(s) nessa data` : "Nenhum vencimento futuro"}
                />
              </Grid>

              <Card title="Programação de câmbio">
                <Flex mb="14px" gap="10px" align="center" justify="space-between" wrap="wrap">
                  <Box overflowX="auto">
                    <SegmentedTabs<ExchangeView>
                      value={view}
                      onChange={reset(setView)}
                      items={viewItems.map((v) => ({ ...v, count: data.counts[v.key] }))}
                    />
                  </Box>
                  <Flex gap="8px">
                    <Button size="sm" variant="outline" leftIcon={<FaFilePdf />} isLoading={exporting === "pdf"} onClick={() => runExport("pdf")}>
                      PDF
                    </Button>
                    <Button size="sm" variant="outline" leftIcon={<FaFileExcel />} isLoading={exporting === "xlsx"} onClick={() => runExport("xlsx")}>
                      Excel
                    </Button>
                  </Flex>
                </Flex>
                <DataTable
                  columns={columns}
                  rows={data.items}
                  rowKey={(r) => r.processId}
                  onRowClick={(r) => navigate(openProcessPath(r.processId))}
                  emptyText="Nenhum câmbio nessa visão."
                  server={{ page, pageSize, total: data.total, onPage: setPage, onPageSize: (s) => { setPageSize(s); setPage(1); } }}
                />
                <Text fontSize="11px" color={crm.textFaint} mt="10px">
                  Uma parcela em aberto pode aparecer em mais de uma visão. Concluído = todas as parcelas pagas. Processos encerrados e cancelados ficam fora por padrão.
                </Text>
              </Card>
            </Box>
          )}
        </Box>
      </Layout>
    </Box>
  );
};

export default Cambio;
